import { useEffect, useRef, useState, createElement } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { DefaultOptionType } from 'antd/lib/select';
import { InputRef, TableColumnsType } from 'antd';
import { FilterConfirmProps } from 'antd/lib/table/interface';
import moment from 'moment';
import { DeviceModel, DevicePayload, DeviceSensorModel, DeviceSorterValueType } from 'core/domain/devices/models';
import { useEnvironment } from 'hooks/useEnvironment';
import { useUserSession } from 'hooks/useUserSession';
import { useMessage } from 'hooks/useMessage';
import { useShareSensor } from 'hooks/useShareSensor';
import {
  PeripheralDevicesExpandedColumnsWithActions,
  PeripheralDevicesExpandedColumnsWithActionsTranslations,
} from 'components/organisms/PeripheralDevices/ExpandedColumnsWithActionsPeripheralDevices';
import { ColumnSearchTranslationsType, DeviceDataIndex } from 'components/organisms/PeripheralDevices/SearchPeripheralColumns';
import { getProjectPath, getSharedGatewayPathByGatewayId, SharedGatewayTabType } from 'components/pages/App/routes/projects/config';
import { DATE_TIME_FORMAT_REVERSE_SLASH } from 'constants/date';
import { SorterOrderType } from 'constants/sort';
import { CustomDeviceModel, FilterConditionModel, TableFilterBasicModel, TableFilterBasicOptionModel } from 'utils/globalModels';
import TagError from 'components/atoms/tags/TagError';
import TagSuccess from 'components/atoms/tags/TagSuccess';
import TagDefault from 'components/atoms/tags/TagDefault';
import { TABLE_PAGINATION_PAGE_DEFAULT, TABLE_PAGINATION_SIZE_DEFAULT, TablePaginationSearchValue } from 'constants/table';
import { GetDevicesLists } from 'core/domain/devices/repositories/getDevicesList';
import { ProjectTab } from 'models/project.model';
import { connectivityLiteralsManager, getDevicesListSorterOptions } from 'utils/devices';
import { FilterOperatorsType } from 'constants/filters';
import { getFilters, parseFiltersToBeSent } from 'utils/filter';
import { SelectorSorterModel } from 'components/molecules/TableSorter';
import { DeviceConnectivityType } from 'constants/devices';
import { ColumnsPeripheralDevices } from '../ColumnsPeripheralDevices';
import { TableFilterModel, TableFilterType } from 'components/organisms/TableFilter/models';
import { useCreateDeviceWithoutGateway } from 'functionalities/deviceWithoutGateway/createDevice/resources/useCreateDeviceWithoutGateway';
import { useUpdateDeviceWithoutGateway } from 'functionalities/deviceWithoutGateway/updateDevice/resources/useUpdateDeviceWithoutGateway';
import { useDeleteDeviceWithoutGateway } from 'functionalities/deviceWithoutGateway/deleteDevice/resources/useDeleteDeviceWithoutGateway';
import { useAlertNotification } from 'functionalities/deviceAlertNotifications/resources/useAlertNotification';
import { Device, DeviceSensor } from 'models/devices.model';

const connectivityKey: keyof DevicePayload = 'connectivity';

const getInitialSorterValue = (): SelectorSorterModel => {
  return {
    value: `${DeviceSorterValueType.GATEWAY_ID}.${SorterOrderType.ASC}`,
    label: i18next.t('_DEVICES_LIST_SORTER_LABEL_GATEWAY_ID_ASC'),
  };
};

export const useTabDevices = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { host } = useEnvironment();
  const { token } = useUserSession();
  const page = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.PAGE);
  const size = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.SIZE);
  const sort = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.SORT);
  const filters = new URLSearchParams(window.location.search).get(TablePaginationSearchValue.FILTERS);
  const { setMessageError } = useMessage();
  const { projectId, tab } = useParams<{ projectId: string; tab: ProjectTab }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [devices, setDevices] = useState<CustomDeviceModel[]>([]);
  const [currentDevice, setCurrentDevice] = useState<CustomDeviceModel | null>(null);
  const [searchedColumn, setSearchedColumn] = useState<string>('');
  const [totalPages, setTotalPages] = useState<number>(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  const [currentPage, setCurrentPage] = useState<number>(Number(TABLE_PAGINATION_PAGE_DEFAULT));
  const [currentSize, setCurrentSize] = useState<number>(Number(TABLE_PAGINATION_SIZE_DEFAULT));
  const [currentSortValue, setCurrentSortValue] = useState<SelectorSorterModel>(getInitialSorterValue());
  const [currentFilterValue, setCurrentFilterValue] = useState<string>('');
  const [initialFilters, setInitialFilters] = useState<TableFilterModel[]>([]);
  const [gatewayId, setGatewayId] = useState<string>('');
  const [isMaintenanceActionsDrawerVisible, setIsMaintenanceActionsDrawerVisible] = useState<boolean>(false);
  const [maintenanceDevice, setMaintenanceDevice] = useState<Device>();
  const { shareSensorOptions } = useShareSensor();
  const { createDeviceOptions, successCreatingDevice } = useCreateDeviceWithoutGateway();
  const { updateDeviceOptions, successUpdatingDevice } = useUpdateDeviceWithoutGateway();
  const { deleteDeviceOptions, successDeletingDevice } = useDeleteDeviceWithoutGateway();
  const { alertNotificationOptions, successProcessingAlertNotification } = useAlertNotification();

  const assetIdKey: keyof DevicePayload = 'asset_id';

  const searchInput = useRef<InputRef>(null);

  const peripheralsTitle: string = t('_PROJECT_PERIPHERAL_SHARED_DEVICES_TITLE');

  const sorterLabel: string = t('_SORTER_LABEL');

  const peripheralExpandedColumnsTranslations: PeripheralDevicesExpandedColumnsWithActionsTranslations = {
    nameText: t('name'),
    roomText: t('room'),
    typeText: t('type'),
    usageText: t('usage'),
    actionsTitle: t('actions'),
    shareSensorText: t('_SHARE_SENSOR_TEXT'),
    notReportedText: t('not_reported'),
  };

  const columnSearchTranslations: ColumnSearchTranslationsType = {
    closeButtonText: t('close'),
    resetButtonText: t('_RESET'),
    searchButtonText: t('_SEARCH'),
    placeholder: t('_PLACEHOLDER_GENERAL'),
    notReportedText: t('not_reported'),
  };

  const onSearch = (selectedKeys: string[], confirm: (param?: FilterConfirmProps) => void, dataIndex: DeviceDataIndex) => {
    confirm({ closeDropdown: false });
    setSearchedColumn(dataIndex);
  };

  const onReset = (clearFilters: () => void, confirm: (param?: FilterConfirmProps) => void, dataIndex: DeviceDataIndex) => {
    clearFilters();
    onSearch([''], confirm, dataIndex);
  };

  const onSelectSharedGateway = (gatewayUuid: string) => {
    history.push(getSharedGatewayPathByGatewayId({ projectId, gatewayId: gatewayUuid, tab: SharedGatewayTabType.INFO }));
  };

  const connectivityTagsManager = {
    [DeviceConnectivityType.OFFLINE]: createElement(TagError, { text: t('offline') }),
    [DeviceConnectivityType.ONLINE]: createElement(TagSuccess, { text: t('online') }),
    [DeviceConnectivityType.UNKNOWN]: createElement(TagDefault, { text: t('unknown') }),
  };

  const transformSensorToMaintenanceSensor = ({
    sensorId,
    sensorType,
    name,
    room,
    usage,
    availableTags,
    tags,
  }: DeviceSensorModel): DeviceSensor => {
    return {
      item_name: '',
      item_type: '',
      sensor_id: sensorId,
      sensor_type: sensorType,
      name,
      room,
      usage: usage ?? undefined,
      available_tags: availableTags,
      tags,
    };
  };

  const transformDeviceToMaintenanceDevice = ({
    availableActions,
    batteryLevel,
    deviceId,
    deviceName,
    deviceType,
    enabled,
    parameters,
    sensors,
  }: DeviceModel): Device => {
    const transformedSensors = sensors.map((sensor) => transformSensorToMaintenanceSensor(sensor)) ?? [];
    return {
      available_actions: availableActions,
      battery_level: batteryLevel,
      device_id: deviceId,
      device_name: deviceName,
      device_type: deviceType,
      enabled,
      parameters,
      password: '',
      thing_id: '',
      sensors: transformedSensors,
    };
  };

  const onOpenMaintenanceActionsDrawer = (selectedDevice: CustomDeviceModel) => {
    !!selectedDevice.gatewayUuid && setGatewayId(selectedDevice.gatewayUuid);
    setIsMaintenanceActionsDrawerVisible(true);
    const newMaintenanceDevice = transformDeviceToMaintenanceDevice(selectedDevice);
    setMaintenanceDevice(newMaintenanceDevice);
  };

  const columns = ColumnsPeripheralDevices({
    columnSearchTranslations,
    searchInput,
    connectivityTagsManager,
    onSearch,
    onReset,
    onSelectSharedGateway,
    onOpenMaintenanceActionsDrawer,
    onOpenUpdateDeviceDrawer: (device: CustomDeviceModel) => updateDeviceOptions.onOpenUpdateDeviceDrawer(device),
    onOpenDeleteDeviceModal: (device: CustomDeviceModel) => deleteDeviceOptions.onOpenDeleteDeviceModal(device),
    onDeleteAlertNotificationDrawer: (device: CustomDeviceModel) => alertNotificationOptions.onOpenDeleteAlertNotification(device),
    onOpenAlertNotificationDrawer: (device: CustomDeviceModel) => alertNotificationOptions.onOpenAlertNotificationDrawer(device),
  });

  const onCloseMaintenanceActionsDrawer = () => {
    setIsMaintenanceActionsDrawerVisible(false);
    setMaintenanceDevice(undefined);
    setGatewayId('');
  };

  const maintenanceActionsDrawerOptions = {
    gatewayId,
    maintenanceDevice,
    onCloseMaintenanceActionsDrawer,
    isMaintenanceActionsDrawerVisible,
  };

  const onOpenShareSensorModal = (sensorUuid: string) => {
    !!currentDevice &&
      !!currentDevice.gatewayUuid &&
      shareSensorOptions.onOpenShareSensorModal({ sensorUuid, gatewayId: currentDevice.gatewayUuid });
  };

  const expandedColumns: TableColumnsType<DeviceSensorModel> = PeripheralDevicesExpandedColumnsWithActions({
    translations: peripheralExpandedColumnsTranslations,
    onOpenShareSensorModal,
  });

  const transformDevices = (data: DeviceModel[]): CustomDeviceModel[] => {
    return data.map((device) => {
      const gatewayId = !!device.gatewayUuid ? `:${device.gatewayUuid}` : '';
      const deviceId = `${device.deviceId}${gatewayId}`;
      const connectivity = !!device.connectivity ? device.connectivity : DeviceConnectivityType.UNKNOWN;
      const updatedAtLiteral = !!device.updatedAt
        ? moment.unix(device.updatedAt).format(DATE_TIME_FORMAT_REVERSE_SLASH)
        : t('not_reported');

      return { ...device, deviceId, connectivity, updatedAtLiteral };
    });
  };

  const onExpandDevice = (expanded: boolean, device: CustomDeviceModel) => {
    setCurrentDevice(expanded ? device : null);
  };

  const parseSortValues = (currentSort: string) => {
    const [field, order] = currentSort.split('.') as [DeviceSorterValueType, SorterOrderType];
    return [{ field, order }];
  };

  const getSortValues = (sortValue: string | null): SelectorSorterModel | null => {
    return getDevicesListSorterOptions().find((sort) => sort.value === sortValue) || null;
  };

  const getData = async ({
    currentPageNumber,
    currentPageSize,
    currentSort,
    currentFilter,
  }: {
    currentPageNumber: number;
    currentPageSize: number;
    currentSort: string;
    currentFilter: string;
  }) => {
    setLoading(true);
    try {
      const sort = parseSortValues(currentSort);
      const defaultFilter: FilterConditionModel[] = [{ field: assetIdKey, operator: FilterOperatorsType.NULL, value: '' }];
      const filters = parseFiltersToBeSent(currentFilter, defaultFilter);
      const response = await GetDevicesLists({
        host,
        token,
        projectId,
        page: currentPageNumber,
        size: currentPageSize,
        sort,
        filters,
      });
      const transformedDevices = transformDevices(response.data);
      setDevices(transformedDevices);
      setTotalPages(response.meta.totalPages);
    } catch (error) {
      setMessageError({ description: t('_DEVICES_LIST_ERROR_MESSAGE') });
    } finally {
      setLoading(false);
    }
  };

  const onChangePage = (pageNumber: number, pageSize?: number) => {
    history.push(
      getProjectPath({
        projectId,
        tab,
        page: String(pageNumber),
        size: String(pageSize!),
        sort: currentSortValue.value,
        filters: currentFilterValue,
      })
    );
  };

  const onChangeSorter = (value: string, option: DefaultOptionType | DefaultOptionType[]) => {
    history.push(
      getProjectPath({
        projectId,
        tab,
        page: String(currentPage),
        size: String(currentSize),
        sort: value,
        filters: currentFilterValue,
      })
    );
  };

  const sorterOptions = {
    label: sorterLabel,
    value: currentSortValue,
    valueOptions: getDevicesListSorterOptions(),
    onChangeSorter,
    disabled: loading,
    loading: loading,
  };

  const navigateToDefaultValues = (newPage: string, newSize: string, newSort: SelectorSorterModel, newFilters: string) => {
    const defaultRoute = getProjectPath({ projectId, tab, page: newPage, size: newSize, sort: newSort.value, filters: newFilters });
    history.replace(defaultRoute);
  };

  const updateParamsValues = (newPage: string, newSize: string, newSort: SelectorSorterModel, newFilters: string) => {
    setCurrentPage(Number(newPage));
    setCurrentSize(Number(newSize));
    setCurrentSortValue(newSort);
    setCurrentFilterValue(newFilters);
  };

  const onApplyFilters = (url: string) => {
    history.push(
      getProjectPath({
        projectId,
        tab,
        page: String(currentPage),
        size: String(currentSize),
        sort: currentSortValue.value,
        filters: url,
      })
    );
  };

  const getCustomFilters = (currentFilters: string) => {
    const items: TableFilterBasicModel[] = [
      {
        key: connectivityKey,
        label: t('_ASSET_DEVICE_COLUMN_CONNECTIVITY_TITLE'),
        type: TableFilterType.CHECKBOX,
      },
    ];
    const options: TableFilterBasicOptionModel[] = Object.values(DeviceConnectivityType).map((option) => ({
      key: connectivityKey,
      value: option,
      label: connectivityLiteralsManager[option](),
    }));

    const customInitialFilters = getFilters({ items, options, currentFilters });

    setInitialFilters(customInitialFilters);
  };

  useEffect(() => {
    const newPage = page || TABLE_PAGINATION_PAGE_DEFAULT;
    const newSize = size || TABLE_PAGINATION_SIZE_DEFAULT;
    const newSort = getSortValues(sort);
    const parsedSort = newSort || getInitialSorterValue();
    const newFilters = filters || '';

    (!page || !size || !newSort) && navigateToDefaultValues(newPage, newSize, parsedSort, newFilters);

    if (!!page && !!size && !!newSort) {
      getData({
        currentPageNumber: Number(page),
        currentPageSize: Number(newSize),
        currentSort: parsedSort.value,
        currentFilter: newFilters,
      });
      updateParamsValues(newPage, newSize, parsedSort, newFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, sort, filters]);

  useEffect(() => {
    if (successCreatingDevice) {
      navigateToDefaultValues(TABLE_PAGINATION_PAGE_DEFAULT, TABLE_PAGINATION_SIZE_DEFAULT, currentSortValue, currentFilterValue);
      getData({
        currentPageNumber: Number(TABLE_PAGINATION_PAGE_DEFAULT),
        currentPageSize: Number(TABLE_PAGINATION_SIZE_DEFAULT),
        currentSort: currentSortValue.value,
        currentFilter: currentFilterValue,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successCreatingDevice]);

  useEffect(() => {
    (successUpdatingDevice || successDeletingDevice || successProcessingAlertNotification) &&
      getData({
        currentPageNumber: currentPage,
        currentPageSize: currentSize,
        currentSort: currentSortValue.value,
        currentFilter: currentFilterValue,
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successUpdatingDevice, successDeletingDevice, successProcessingAlertNotification]);

  useEffect(() => {
    getCustomFilters(currentFilterValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFilterValue]);

  return {
    devices,
    peripheralsTitle,
    searchedColumn,
    columns,
    expandedColumns,
    createDeviceOptions,
    updateDeviceOptions,
    deleteDeviceOptions,
    alertNotificationOptions,
    maintenanceActionsDrawerOptions,
    totalPages,
    currentPage,
    currentSize,
    shareSensorOptions,
    sorterOptions,
    initialFilters,
    gatewayId,
    onChangePage,
    onExpandDevice,
    onApplyFilters,
    loading,
  };
};
